import {
  BottomSheet,
  DialogFooter,
  Input,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, FormLabel, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { type ChartingCredential } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export interface UpdateChartingCredentialsBottomSheetProps {
  modalState: UseModalState;
  shiftId?: string;
  workplaceId: string;
  workerId: string;
  showPassword?: boolean;
  chartingCredentials?: ChartingCredential;
  defaultCredentials?: ChartingCredential;
  isSaving?: boolean;
  onSave?: (username: string, password: string) => void;
  onClose?: () => void;
}

export function UpdateChartingCredentialsBottomSheet({
  modalState,
  shiftId,
  workplaceId,
  workerId,
  showPassword = false,
  chartingCredentials,
  defaultCredentials,
  isSaving,
  onSave,
  onClose,
}: UpdateChartingCredentialsBottomSheetProps) {
  const initialCredentials = defaultCredentials ?? chartingCredentials;
  const [username, setUsername] = useState(initialCredentials?.username ?? "");
  const [password, setPassword] = useState(initialCredentials?.password ?? "");

  useEffect(() => {
    const credentialsToUse = defaultCredentials ?? chartingCredentials;
    setUsername(credentialsToUse?.username ?? "");
    setPassword(credentialsToUse?.password ?? "");
  }, [defaultCredentials, chartingCredentials]);

  const logMetadata = {
    shiftId,
    workplaceId,
    workerId,
  };

  const handleSave = () => {
    // Determine the type of save operation
    const hasExistingCredentials =
      isDefined(chartingCredentials?.username) && isDefined(chartingCredentials?.password);
    const credentialsChanged =
      hasExistingCredentials &&
      (username !== chartingCredentials?.username || password !== chartingCredentials?.password);

    let eventName: string;
    if (!hasExistingCredentials) {
      eventName = APP_V2_USER_EVENTS.CHARTING_CREDENTIALS_UPDATE_SAVE_NEW_CREDENTIALS;
    } else if (credentialsChanged) {
      eventName = APP_V2_USER_EVENTS.CHARTING_CREDENTIALS_UPDATE_SAVE_UPDATED_CREDENTIALS;
    } else {
      eventName = APP_V2_USER_EVENTS.CHARTING_CREDENTIALS_UPDATE_SAVE_UNCHANGED_CREDENTIALS;
    }

    logEvent(eventName, logMetadata);
    onSave?.(username, password);
  };

  const handleClose = () => {
    logEvent(APP_V2_USER_EVENTS.CHARTING_REMINDER_VIEW_MY_LOGIN, logMetadata);
    modalState.closeModal();
    onClose?.();
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet
        canDismiss
        modalState={modalState}
        header={<BottomSheetHeader onClose={handleClose} />}
        onClose={handleClose}
      >
        <Box sx={{ paddingX: 2, display: "flex", flexDirection: "column", gap: 3 }}>
          <Text bold variant="h3" sx={{ textAlign: "center" }}>
            {(chartingCredentials?.username ?? chartingCredentials?.password)
              ? "Update charting login"
              : "Save charting login"}
          </Text>

          <Text variant="body1" sx={{ paddingX: 10, textAlign: "center" }}>
            Keep your login details up to date for future shifts at this workplace.
          </Text>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 5, paddingX: 4 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel htmlFor="username-input">Username</FormLabel>
              <Input
                fullWidth
                id="username-input"
                value={username}
                size="medium"
                onChange={(event) => {
                  setUsername(event.target.value);
                }}
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel htmlFor="password-input">Password</FormLabel>
              <Input
                fullWidth
                secret={!showPassword}
                id="password-input"
                value={password}
                size="medium"
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
            </Box>
          </Box>
        </Box>

        <DialogFooter orientation="vertical" LinkComponent={Link} sx={{ marginTop: 8 }}>
          <Button
            fullWidth
            size="large"
            role="button"
            disabled={!username.trim() || !password.trim() || isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogFooter>
      </BottomSheet>
    </ThemeProvider>
  );
}
